import { FormattedDate, FormattedMessage } from 'react-intl';
import { LeafIcon } from '@synergy/theme-next';
import { PoolActionLabel, PoolRow, PoolTooltip, PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import { Stack, Tooltip, styled } from '@mui/material';

import { VerticalDivider } from '../../../components/vertical-divider/vertical-divider';
import { useArticleDetailContext } from '../article-detail';

const StyledStack = styled(Stack)(({ theme }) => ({
    ...theme.typography.body2,
    flexWrap: 'wrap',
    color: theme.palette.grey[800],
    flexDirection: 'row',
    rowGap: theme.spacing(0.5),
    columnGap: theme.spacing(1),
}));

export const ArticleDetailTitle = (): ReactElement | null => {
    const { article } = useArticleDetailContext();
    const { name, brand, globalTradeItemNumber, number, manufacturerNumber } = article;

    return (
        <Stack gap={{ xs: 0.5, sm: 1 }} alignItems="flex-start">
            <PoolTypography variant="h3">{name}</PoolTypography>
            <StyledStack divider={<VerticalDivider />}>
                {brand && (
                    <span>
                        <FormattedMessage id="app.article.detail.brand" /> {brand}
                    </span>
                )}
                <FormattedMessage id="app.article.detail.articleNumber" values={{ number }} />
                {globalTradeItemNumber && (
                    <span>
                        <FormattedMessage id="app.article.detail.gtin" /> {globalTradeItemNumber}
                    </span>
                )}
                {manufacturerNumber && (
                    <span>
                        <FormattedMessage id="app.article.detail.manufacturerNumberShort" /> {manufacturerNumber}
                    </span>
                )}
            </StyledStack>
            {article.isAvailable && (
                <PoolRow gap={1} sx={{ mt: { xs: 1.5, sm: 1 } }}>
                    {!!article.specialPrice && (
                        <PoolTooltip
                            title={
                                <FormattedMessage
                                    id="app.specialOffer.tooltip"
                                    values={{
                                        date: (
                                            <FormattedDate
                                                value={article.specialPrice.endDate}
                                                year="numeric"
                                                month="short"
                                                day="2-digit"
                                            />
                                        ),
                                    }}
                                />
                            }
                        >
                            <PoolActionLabel variant="small" color="error" hasSkew={false}>
                                <FormattedMessage id="app.article.specialPrice" />
                            </PoolActionLabel>
                        </PoolTooltip>
                    )}
                    {article.isBestPrice && (
                        <PoolActionLabel variant="small" color="secondary" hasSkew={false}>
                            <FormattedMessage id="app.article.detail.bestPrice" />
                        </PoolActionLabel>
                    )}
                    {article.isEcoProduct && (
                        <Tooltip title={<FormattedMessage id="app.tooltip.ecoProduct" />}>
                            <Stack>
                                <PoolActionLabel variant="small" color="success" hasSkew={false}>
                                    <LeafIcon sx={{ fontSize: 16 }} />
                                </PoolActionLabel>
                            </Stack>
                        </Tooltip>
                    )}
                </PoolRow>
            )}
        </Stack>
    );
};
